import type { IUseCase } from '@/shared/contracts/use-case.contract';
import type { IHttpClient } from '@/shared/contracts/http-client.contract';
import { HttpMethod } from '@/shared/contracts/http-client.contract';

import { UIError } from '@/shared/utils/ui-error';

import { ENDPOINT } from '../types/make-order';
import type { MakeOrderItem } from '../types/make-order';
import {
  getOrderAddonId,
  isComboOrderItem,
  rebuildComboChoices,
} from '../utils/rebuild-combo-choices';
import { buildComboChoicesPayload } from '@/modules/stores/utils/combo';
import {
  ENDPOINT as STORE_ITEMS_ENDPOINT,
  type GetStoreItemsResponse,
  type StoreItem,
} from '@/modules/stores/types/get-store-items';

import {
  CARD_PAYMENT_METHOD_ID,
  type ChangePaymentMethodRequest,
  type ChangePaymentMethodResponse,
  type ReplaceOrderBody,
} from '../types/change-payment-method';

type UseCaseInput = ChangePaymentMethodRequest;
type UseCaseOutput = ChangePaymentMethodResponse;

/**
 * Troca o método de pagamento de um pedido existente.
 *
 * Usado no fluxo de melhoria de UX para falha/recusa no pagamento: quando uma cobrança
 * (ex: PIX ou cartão) é recusada, o cliente pode reescolher a forma de pagamento sem
 * precisar refazer o pedido inteiro.
 *
 * Não há rota dedicada para isto: a antiga `PATCH .../orders/{id}/payment-method` não
 * existe no backend e respondia 404 (issue 288). Em vez disso, **reenviamos a própria
 * rota de criação de pedido (place-order)** com os mesmos itens/entrega do pedido
 * original, trocando apenas a forma de pagamento. O backend reprocessa a cobrança e
 * retorna o pedido atualizado, permitindo que a UI retome o polling do status.
 */
export class ChangePaymentMethodUseCase implements IUseCase<UseCaseInput, UseCaseOutput> {
  private client: IHttpClient;

  constructor(client: IHttpClient) {
    this.client = client;
  }

  async execute(input: UseCaseInput): Promise<UseCaseOutput> {
    const { order, payment_method, cash_change_amount, payment_mode } = input;

    if (!order?.id_order) {
      throw new UIError('ORDER_NOT_FOUND', 'Pedido não encontrado.');
    }

    const storeSlug = order.merchant?.slug;
    if (!storeSlug) {
      throw new UIError(
        'MERCHANT_SLUG_REQUIRED',
        'Não foi possível identificar a loja do pedido.'
      );
    }

    if (!order.items || order.items.length === 0) {
      throw new UIError('ITEMS_REQUIRED', 'O pedido precisa conter pelo menos um item.');
    }

    if (!payment_method || payment_method.id === '') {
      throw new UIError(
        'PAYMENT_METHOD_REQUIRED',
        'É obrigatório informar um método de pagamento.'
      );
    }

    // Pagamento com cartão de crédito (ID 900) exige um cartão salvo.
    const isCardPayment =
      payment_method.id === CARD_PAYMENT_METHOD_ID ||
      payment_method.id === String(CARD_PAYMENT_METHOD_ID);

    if (isCardPayment && !payment_method.card_id) {
      throw new UIError(
        'CARD_REQUIRED',
        'É obrigatório selecionar um cartão para pagamento com cartão de crédito.'
      );
    }

    if (
      payment_method.installments !== undefined &&
      (payment_method.installments < 1 || payment_method.installments > 12)
    ) {
      throw new UIError(
        'INVALID_INSTALLMENTS',
        'O número de parcelas deve estar entre 1 e 12.'
      );
    }

    // Reconstrói os itens do pedido original no formato aceito pela rota de criação.
    // Combo configurável (PRAT-20): a linha de combo vai com `addons: []` e as
    // escolhas reconstruídas em `combo_choices` — as linhas gravadas de escolha
    // são só exibição e nunca são reenviadas como complemento.
    const catalog = order.items.some(isComboOrderItem)
      ? await this.loadCatalog(storeSlug)
      : new Map<string, StoreItem>();

    const items: MakeOrderItem[] = order.items.map(item => {
      if (isComboOrderItem(item)) {
        const comboChoices = rebuildComboChoices(item, catalog.get(item.item_id));
        return {
          id: item.item_id,
          quantity: item.quantity,
          observation: '',
          addons: [],
          ...(comboChoices.length
            ? { combo_choices: buildComboChoicesPayload(comboChoices) }
            : {}),
        };
      }

      return {
        id: item.item_id,
        quantity: item.quantity,
        observation: '',
        addons: item.order_item_addons.map(addon => ({
          id: getOrderAddonId(addon) ?? '',
          addon_name: addon.addon_name,
          addon_quantity: addon.addon_quantity,
        })),
      };
    });

    const body: ReplaceOrderBody = {
      items,
      payment_info: {
        payment_methods: [payment_method],
        cash_change_amount,
      },
      cash_change_amount: cash_change_amount ?? 0,
      delivery_info: {
        delivery_type: order.delivery_type,
      },
      payment_mode,
      store_slug: storeSlug,
    };

    return this.client.sendRequest<UseCaseOutput, ReplaceOrderBody>({
      endpoint: ENDPOINT(storeSlug),
      method: HttpMethod.POST,
      body,
    });
  }

  // Cardápio atual da loja, indexado por produto. Só é buscado quando há combo
  // com escolhas; sem ele (falha de rede) a reconstrução usa o que veio no pedido.
  private async loadCatalog(storeSlug: string): Promise<Map<string, StoreItem>> {
    const catalog = new Map<string, StoreItem>();
    try {
      const response = await this.client.sendRequest<GetStoreItemsResponse, never>({
        method: HttpMethod.GET,
        endpoint: `${STORE_ITEMS_ENDPOINT}/${storeSlug}/items`,
      });
      for (const categoryItems of Object.values(response?.items ?? {})) {
        if (!Array.isArray(categoryItems)) continue;
        for (const storeItem of categoryItems) {
          if (storeItem?.id) catalog.set(storeItem.id, storeItem);
        }
      }
    } catch {
      // Segue sem cardápio: as escolhas saem de `combo_choices` do pedido.
    }
    return catalog;
  }
}
